import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export const LeadDialog = ({ trigger, type = "quote", productName = "", title, description }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/inquiries", { ...form, type, product_name: productName });
      toast.success(data.message || "Request received.");
      setForm({ name: "", email: "", phone: "", message: "" });
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#FFFFFF] border-[#ECEAE4] rounded-none sm:max-w-md" data-testid="lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-medium text-[#2C2A29]">
            {title || (productName ? `Request a Quote` : "Start Your Project")}
          </DialogTitle>
          <DialogDescription className="text-[#5E5A54] font-light">
            {description || (productName ? `Enquire about ${productName}. We reply within one business day.` : "Tell us about your project and our sourcing team will be in touch.")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label className="label-eyebrow text-[#5E5A54]">Name</Label>
            <Input required value={form.name} onChange={set("name")} className="rounded-none bg-white border-[#ECEAE4]" data-testid="lead-name-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="label-eyebrow text-[#5E5A54]">Email</Label>
              <Input required type="email" value={form.email} onChange={set("email")} className="rounded-none bg-white border-[#ECEAE4]" data-testid="lead-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow text-[#5E5A54]">Phone</Label>
              <Input value={form.phone} onChange={set("phone")} className="rounded-none bg-white border-[#ECEAE4]" data-testid="lead-phone-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow text-[#5E5A54]">Project details</Label>
            <Textarea value={form.message} onChange={set("message")} rows={4} className="rounded-none bg-white border-[#ECEAE4]" placeholder="Share your floor plan, product list, style references and budget." data-testid="lead-message-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-none bg-[#2C2A29] hover:bg-[#4A4641] text-[#FFFFFF] h-12 tracking-wide" data-testid="lead-submit-button">
            {loading ? "Sending…" : "Send Enquiry"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
